import * as Yup from "yup";
import { ARABIC_REGEX, ENGLISH_REGEX } from "../Patterns";

// nullable() so a `null` from the API reports "required", not Yup's type error.
const requiredText = (message = "generalFieldRequired") =>
  Yup.string().nullable().trim().required(message);

/** Present-value check for selects (id), dates (string) and any non-string field. */
const requiredValue = (message = "generalFieldRequired") =>
  Yup.mixed().test(
    "required",
    message,
    (value) => value !== null && value !== undefined && value !== "",
  );

/**
 * Edit Applicant Profile schema. Every applicant field is mandatory except
 * Gender, Date of Birth, Matloob Number, Company Worked With and the document
 * rows (PROLINE-3435). Conditionally-rendered fields are only included when the
 * caller says they are on screen.
 */
export const validateEditApplicantSchema = ({
  isSaudi = false,
  hasChronic = false,
  hasDisability = false,
  isStudyFieldOther = false,
} = {}) => {
  const shape = {
    firstName: requiredText().matches(ENGLISH_REGEX, {
      message: "englishLettersOnly",
      excludeEmptyString: true,
    }),
    middleName: requiredText().matches(ENGLISH_REGEX, {
      message: "englishLettersOnly",
      excludeEmptyString: true,
    }),
    lastName: requiredText().matches(ENGLISH_REGEX, {
      message: "englishLettersOnly",
      excludeEmptyString: true,
    }),
    firstNameAr: requiredText().matches(ARABIC_REGEX, {
      message: "arabicLettersOnly",
      excludeEmptyString: true,
    }),
    middleNameAr: requiredText().matches(ARABIC_REGEX, {
      message: "arabicLettersOnly",
      excludeEmptyString: true,
    }),
    lastNameAr: requiredText().matches(ARABIC_REGEX, {
      message: "arabicLettersOnly",
      excludeEmptyString: true,
    }),

    nationalityId: requiredValue(),
    cityId: requiredValue(),
    roleId: requiredValue(),
    bankId: requiredValue(),
    studyFieldId: requiredValue(),

    countryCode: requiredText(),
    phoneNumber: requiredText(),
    height: requiredText(),
    weight: requiredText(),
    stcNumber: requiredText(),
    cardHolderName: requiredText(),

    // A Saudi IBAN is "SA" + 22 digits = 24 characters total (ISO 13616).
    iban: requiredText()
      .test(
        "iban-starts-with-sa",
        "ibanMustStartWithSA",
        (value) => !value || value.startsWith("SA"),
      )
      .test(
        "iban-exact-length",
        "ibanExactLength",
        (value) => !value || value.length === 24,
      ),

    isNotifiable: Yup.mixed().test(
      "required",
      "generalFieldRequired",
      (value) => value === true || value === false,
    ),

    // Matloob number is optional, but a 9-character national request number when given.
    matloobNumber: Yup.string()
      .nullable()
      .trim()
      .test(
        "matloob-exact-length",
        "matloobNumberWrongLength",
        (value) => !value || value.length === 9,
      ),
  };

  if (isSaudi) {
    shape.nationalId = requiredText();
    shape.nationalExpireDate = requiredValue("requiredDate");
  } else {
    shape.iqamaNumber = requiredText();
    shape.iqamaExpireDate = requiredValue("requiredDate");
  }

  if (isStudyFieldOther) {
    shape.fieldStudyName = requiredText();
  }
  if (hasChronic) {
    shape.chronic = Yup.array().min(1, "generalFieldRequired");
  }
  if (hasDisability) {
    shape.disability = Yup.array().min(1, "generalFieldRequired");
  }

  return Yup.object().shape(shape);
};
